import { Trans } from '@lingui/macro'
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { Box, Grid, InputAdornment, SvgIcon, SxProps, TextField, Tooltip } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { baseMUITheme } from 'app/theme/themeBase'
import { useTheme } from 'app/theme/ThemeProvider'
import { isNil } from 'lodash'
import MUIDataTable, { MUIDataTableOptions, MUIDataTableProps } from 'mui-datatables'
import React, { FC, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FilterCard, FilterOption } from '../resources/resources-list/FilterCard'
import './CustomDataTable.scss'
import FilterChips from './FilterChips'

const datatableOverrides = {
  components: {
    MUIDataTable: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 0,
          boxShadow: 'none',
          gap: 0
        })
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme, ...props }) => ({
          fontSize: '15px',
          lineHeight: '25px',
          fontWeight: 400,
          verticalAlign: 'middle',
          textAlign: 'left',
          padding: '16px 16px',
          '&.MuiTableCell-body': {
            verticalAlign: 'middle'
          },
          '&.MuiTableCell-head': {
            verticalAlign: 'top',
            alignItems: 'flex-start'
          },
          '&.non-sortable': {
            paddingTop: '22px'
          },
          '&:first-of-type': {
            padding: '16px 0px 16px 32px !important',
            [theme.breakpoints.down('md')]: {
              padding: '16px 16px 16px 16px !important'
            }
          },
          '&:last-of-type': {
            paddingRight: '32px !important'
          },
          [theme.breakpoints.down('md')]: {
            padding: '16px 16px 16px 16px !important',
            fontSize: '14px',
            display: 'flex',
            flexDirection: 'row !important',
            alignItems: 'center'
          }
        }),
        head: {
          fontSize: '15px',
          fontWeight: 400,
          lineHeight: '25px',
          textAlign: 'left',
          verticalAlign: 'top',
          padding: '16px 16px',
          backgroundColor: '#ffffff'
        }
      }
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          textAlign: 'left',
          [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
            fontSize: '14px'
          }
        }),
        stackedHeader: {
          fontWeight: 'bold'
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          verticalAlign: 'top',
          textAlign: 'left'
        },
        fixedHeader: {
          verticalAlign: 'top'
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderRadius: '4px',
          '&:hover': {
            background: 'rgba(23, 55, 79, 0.10)',
            borderRadius: '4px',
            color: '#17374F'
          },
          "&[data-testid='Filter Table-iconButton']:hover": {
            background: 'rgba(23, 55, 79, 0.10)',
            color: '#17374F !important'
          },
          "&[data-testid='Filter Table-iconButton']": {
            // position: 'absolute',
            // right: '32px',
            // top: '-54px',
            // transform: 'translateY(-50%)'
          }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        },
        toolbar: {
          // overflow: 'hidden'
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        // root: {
        //   border: 'none !important',
        //   minHeight: '12px !important',
        //   height: '32px',
        //   padding: 0,
        //   '@media (min-width: 600px)': {
        //     minHeight: '12px !important'
        //   }
        // }
      }
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          lineHeight: 'inherit',
          padding: 0,
          display: 'flex'
        },
        icon: {
          width: '24px',
          height: '24px',
          fontSize: '24px',
          verticalAlign: 'middle',
          marginTop: 0,
          position: 'relative',
          marginLeft: '20px',
          top: '2px'
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: '35px',
          height: '35px',
          padding: '8px 10px'
        }
      }
    },
    MUIDataTableFilterList: {
      styleOverrides: {
        root: {
          display: 'none'
        }
      }
    }
  }
}

type FilterFunction = (value: any, selectedValues: any[]) => boolean

interface FilteredColumnData {
  key: string
  values?: Array<{
    key: string
    label: string
    value: string
    displayed?: boolean
  }>
  filterFn?: FilterFunction
  filterTitle?: JSX.Element | string
  displayFilterWhenOnlyOneEmptyOption?: boolean
  sort?: 'alphabetical'
}

interface CustomDataTableProps extends MUIDataTableProps {
  columns: any[]
  showBaseFilters?: boolean
  options: MUIDataTableOptions
  data: any[]
  filteredColumns?: FilteredColumnData[]
  pagination?: boolean
  title?: React.ReactNode | null
  onFilterChange?: (
    key: string,
    filters: FilterOption[],
    setFilters: React.Dispatch<React.SetStateAction<Record<string, string[]>>>
  ) => void
  initialFilters?: Record<string, string[]>
  sx?: SxProps
  useUrlFilters?: boolean
}

/**
 * A customized table component based on the MUI DataTable,which allows displaying data
 * in a table with search functionality. Additionally, the component customizes
 * the MUI theme and styles for various elements.
 *
 * @component
 *
 * @param {object} props - The component's props.
 * @param {React.ReactNode | null} [props.title=null] - Optional title for the table.
 * @param {Array} props.columns - The columns for the table, defining data and options for each column.
 * @param {Array} props.data - The data to be displayed in the table.
 * @param {object} props.options - Configuration options for the table, such as search, filtering, etc.
 * @param {function} props.options.onFilterChange - Optional callback function to be called when the filter changes.
 * @param {boolean} [props.useUrlFilters=false] - Optional flag. If true, filter values are stored in the URL
 * as query parameters to preserve them after page refresh.
 *
 * @returns {JSX.Element} - Renders the `CustomDataTable` component with the table and an optional search field.
 */
const CustomDataTable = ({
  columns,
  showBaseFilters,
  options,
  data,
  filteredColumns = [],
  title = null,
  initialFilters = {},
  sx = {},
  pagination = false,
  useUrlFilters = false,
  ...props
}: CustomDataTableProps): JSX.Element => {
  const { mode } = useTheme() ?? { mode: 'light' }
  const [searchTerm, setSearchTerm] = useState('')
  const location = useLocation()
  const history = useHistory()
  const parseFiltersFromUrl = (): Record<string, string[]> => {
    const params = new URLSearchParams(location.search)

    const result: Record<string, string[]> = {}

    params.forEach((value, key) => {
      if (!result[key]) {
        result[key] = []
      }
      result[key].push(value)
    })

    return result
  }

  const [filters, setFilters] = useState<Record<string, string[]>>(() => {
    if (!useUrlFilters) return initialFilters
    const urlFilters = parseFiltersFromUrl()
    return { ...initialFilters, ...urlFilters }
  })

  const customSearch = options.search !== false && options.customSearchTop !== true
  const customSearchTop = options.customSearchTop === true

  const baseTheme = baseMUITheme(mode)
  const datatableTheme = createTheme(baseTheme, datatableOverrides)

  const handleSearchChange = (value: string): void => {
    setSearchTerm(value)
    if (options.onSearchChange) {
      options.onSearchChange(value)
    }
  }

  const syncFiltersToUrl = (nextFilters: Record<string, string[]>): void => {
    const params = new URLSearchParams(location.search)

    // remove old filters
    Object.keys(filters).forEach((key) => {
      params.delete(key)
    })

    // add new filters
    Object.entries(nextFilters).forEach(([key, values]) => {
      values.forEach((v) => {
        if (v) params.append(key, v)
      })
    })

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
      state: { useUrlFilters: true }
    })
  }

  const updateFilters = (updater: React.SetStateAction<Record<string, string[]>>): void => {
    setFilters((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater

      if (useUrlFilters) {
        syncFiltersToUrl(next)
      }

      return next
    })
  }

  const getAvailableFilterValues = (key: string, data: any[]): string[] => {
    const values = Array.from(
      new Set(
        data
          .map((row) => (Array.isArray(row[key]) ? row[key] : [row[key] ?? '']))
          .flat()
          .filter((value) => value !== '')
      )
    )
    const column = filteredColumns.find((col) => col.key === key)
    if (isNil(column?.values)) {
      return values
    }
    const sortedValues = column.values
      .filter((value) => values.includes(value.key))
      .map((value) => value.key)
    return sortedValues
  }

  const filteredData = pagination
    ? data
    : data.filter((row) =>
        Object.entries(filters).every(([key, selectedValues]) => {
          const columnFilter = filteredColumns.find((col) => col.key === key)
          if (!isNil(columnFilter?.filterFn)) {
            return columnFilter.filterFn(row[key], selectedValues)
          }
          if (selectedValues.length === 0) return true
          if (selectedValues.length === 0) return true
          if (Array.isArray(row[key])) {
            return row[key].some((value) => selectedValues.includes(value))
          }
          if (Array.isArray(selectedValues)) {
            return selectedValues.includes(row[key])
          }
          return !!selectedValues
        })
      )

  const filteredDataForColumnFilter: (columnFilterKey: string) => any[] = (columnFilterKey) => {
    return data.filter((row) => {
      return Object.entries(filters).every(([key, selectedValues]) => {
        const columnFilter = filteredColumns.find((col) => col.key === key)
        if (!isNil(columnFilter?.filterFn)) {
          if (columnFilter.key === columnFilterKey) {
            return true
          }
          return columnFilter.filterFn(row[key], selectedValues)
        }
        if (selectedValues.length === 0) return true
        if (key === columnFilterKey) return true

        if (selectedValues.length === 0) return true
        if (Array.isArray(row[key])) {
          return row[key].some((value) => {
            return Array.isArray(selectedValues) && selectedValues.includes(value)
          })
        }
        if (Array.isArray(selectedValues)) {
          return selectedValues.includes(row[key])
        }
        return !!selectedValues
      })
    })
  }

  const customOptions = {
    ...options,
    search: customSearch || customSearchTop ? false : options.search,
    searchText: searchTerm,
    filter: showBaseFilters ?? false,
    justifyContent: 'space-between',
    onSearchChange:
      customSearch || customSearchTop
        ? (searchQuery: string) => setSearchTerm(searchQuery)
        : (options.onSearchChange ?? (() => {}))
  }

  const usedValuesMap = Object.fromEntries(
    filteredColumns.map((filter) => {
      const uniqueValues = new Set(
        filteredDataForColumnFilter(filter.key)
          .map((item) => item[filter.key])
          .map((value) => (Array.isArray(value) ? value : [value]))
          .flat()
          .filter((value) => value != null)
      )
      return [filter.key, Array.from(uniqueValues)]
    })
  )

  const filtersForChips = Object.fromEntries(
    filteredColumns?.map(({ key, values, sort }) => {
      const filterValues = (values ?? getAvailableFilterValues(key, data)).map((v) =>
        typeof v === 'string' ? { key: v, label: v, filterChip: v } : v
      )

      const filtersArray = filterValues
        .map((value) => ({
          value: value.key,
          label: value.filterChip ?? value.label,
          isChecked: Array.isArray(filters[key]) ? filters[key]?.includes(value.key) : false,
          displayed: value.displayed
        }))
        .filter((obj) => obj.isChecked)

      return [key, filtersArray]
    }) ?? []
  )

  return (
    <ThemeProvider theme={datatableTheme}>
      <Box sx={{ border: '1px solid #17374F', backgroundColor: '#ffffff', ...sx }}>
        {Boolean(title) && filteredColumns.length > 0 && (
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="flex-start"
            sx={{ padding: '16px 32px 16px 32px' }}
          >
            {title}
          </Box>
        )}
        {customSearchTop && (
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="flex-start"
            sx={{ padding: '16px 32px 16px 32px' }}
          >
            <CustomSearch
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleSearchChange={handleSearchChange}
            />
          </Box>
        )}
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ padding: '16px 32px 16px 32px' }}
        >
          <Grid container item alignItems="flex-start" justifyContent="flex-start" gap="24px">
            {filteredColumns.length > 0
              ? filteredColumns.map(({ key, values, sort, filterTitle }) => {
                  let filterValues =
                    pagination && values !== undefined
                      ? values
                      : getAvailableFilterValues(key, data)
                          .map((v) => {
                            return typeof v === 'string'
                              ? {
                                  key: v,
                                  displayed:
                                    filteredColumns
                                      .find((col) => col.key === key)
                                      ?.values?.find((val) => val.key === v)?.displayed ?? true,
                                  label:
                                    filteredColumns
                                      .find((col) => col.key === key)
                                      ?.values?.find((val) => val.key === v)?.label ?? v
                                }
                              : v
                          })
                          .filter((obj) => {
                            return (
                              filters[key]?.includes(obj.key) ||
                              usedValuesMap[key]?.includes(obj.key)
                            )
                          })
                          .filter((obj) => {
                            return obj.displayed
                          })

                  if (sort === 'alphabetical') {
                    filterValues = filterValues.sort((a, b) =>
                      String(a.label).localeCompare(String(b.label))
                    )
                  }

                  const selectedDisplayedFilters = filters?.[key]?.filter((value) => {
                    return filterValues.find((filter) => filter.key === value)?.displayed
                  })

                  const displayFilterWhenOnlyOneEmptyOption = Boolean(
                    filteredColumns.find((col) => col.key === key)
                      ?.displayFilterWhenOnlyOneEmptyOption
                  )
                  if (
                    filterValues?.length < 2 &&
                    selectedDisplayedFilters?.length === 0 &&
                    !displayFilterWhenOnlyOneEmptyOption
                  ) {
                    return null
                  }

                  if (filterValues?.length === 0) {
                    return null
                  }

                  return (
                    <Box key={key}>
                      <FilterCard
                        sx={{ minWidth: '207px' }}
                        filters={filterValues.map((value) => ({
                          key: value.key,
                          label: value.label,
                          isChecked: filters[key]?.includes(value.key) ?? false
                        }))}
                        label={
                          filterTitle ?? (
                            <Trans>{columns.find((col) => col.name === key)?.label ?? key}</Trans>
                          )
                        }
                        onOpen={() => {}}
                        onFilterChange={(newFilters) => {
                          const newFiltersArray = newFilters
                            .filter((f) => f.isChecked)
                            .map((f) => f.key)

                          updateFilters((prev) => ({
                            ...prev,
                            [key]: newFiltersArray
                          }))

                          if (!isNil(options?.onFilterChange)) {
                            options.onFilterChange(key, newFiltersArray, updateFilters)
                          }
                        }}
                      />
                    </Box>
                  )
                })
              : title}
          </Grid>

          {customSearch && (
            <CustomSearch
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleSearchChange={handleSearchChange}
            />
          )}
        </Box>

        {filteredColumns.length > 0 && (
          <Box sx={{ marginLeft: '32px' }}>
            <FilterChips
              filters={filtersForChips}
              onRemoveFilter={(columnKey, filterValue) => {
                const updatedFilters = { ...filters }
                updatedFilters[columnKey] = updatedFilters[columnKey]?.filter(
                  (value) => value !== filterValue
                )
                if (updatedFilters[columnKey].length === 0) {
                  updatedFilters[columnKey] = []
                }
                updateFilters(updatedFilters)
                if (!isNil(options?.onFilterChange)) {
                  options.onFilterChange(columnKey, updatedFilters[columnKey], updateFilters)
                }
              }}
              onClearAllFilters={() => {
                const updatedFilters = { ...filters }
                Object.keys(updatedFilters).forEach((key) => {
                  updatedFilters[key] = []
                })
                updateFilters(updatedFilters)
                if (!isNil(options?.onFilterChange)) {
                  Object.keys(updatedFilters).forEach((key) => {
                    options.onFilterChange(key, [], updateFilters)
                  })
                }
              }}
            />
          </Box>
        )}
        <MUIDataTable columns={columns} options={customOptions} data={filteredData} {...props} />
      </Box>
    </ThemeProvider>
  )
}

interface CustomSearchProps {
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
  handleSearchChange: (value: string) => void
}

const CustomSearch: FC<CustomSearchProps> = ({ searchTerm, setSearchTerm, handleSearchChange }) => {
  return (
    <TextField
      value={searchTerm}
      onChange={(event) => handleSearchChange(event.target.value)}
      variant="outlined"
      sx={{
        display: 'flex',
        width: '405px',
        height: '42px',
        '& fieldset.MuiOutlinedInput-notchedOutline': { border: '1px solid rgba(0, 0, 0, 0.40)' },
        '& .MuiInputBase-root': {
          background: '#FFF',
          height: '42px'
        },
        '& .MuiInputBase-input': {
          padding: '10px 14px'
        }
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {searchTerm !== '' ? (
              <SvgIcon
                component={SearchOffOutlinedIcon}
                sx={{ width: 28, height: 28, cursor: 'pointer' }}
                onClick={() => handleSearchChange('')}
              />
            ) : (
              <Tooltip title="Search">
                <SvgIcon component={SearchOutlinedIcon} sx={{ width: 28, height: 28 }} />
              </Tooltip>
            )}
          </InputAdornment>
        )
      }}
    />
  )
}

export default CustomDataTable
